// Nextmine: klient-hjaelpere til web push.
//
// Abonnementet oprettes i browseren (service worker + PushManager) og gemmes
// paa brugerens profil via /api/push-subscribe. Den offentlige VAPID-noegle
// hentes fra serveren, saa den ikke skal bages ind i bundlet.
//
// iPhone-detalje: iOS understoetter kun web push naar sitet er lagt paa
// hjemmeskaermen (PWA) - i almindelig Safari findes PushManager slet ikke.

import { apiBaseUrl } from './api';

const urlBase64ToUint8Array = base64String => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
};

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const getPushPermission = () =>
  isPushSupported() ? Notification.permission : 'unsupported';

// Nuvaerende abonnement paa denne enhed, eller null.
export const getExistingPushSubscription = async () => {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) return null;
  return reg.pushManager.getSubscription();
};

// iOS-KRITISK opdeling: pushManager.subscribe() SKAL kaldes mens brugerens
// klik stadig taeller som "user gesture". Al netvaerksarbejde (noegle + SW-
// registrering) skal derfor ske FOER klikket - ellers udloeber gestussen paa
// iOS mens vi venter, og subscribe afvises med NotAllowedError.
// preparePush() koeres ved sideindlaesning; subscribeToPush() i selve klikket.
export const preparePush = async () => {
  if (!isPushSupported()) throw new Error('unsupported');

  const keyRes = await fetch(`${apiBaseUrl()}/api/push-public-key`);
  if (!keyRes.ok) throw new Error('push-not-configured');
  const { publicKey } = await keyRes.json();

  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;

  return { reg, publicKey };
};

// Fuldt tilmeldingsflow. prepared = resultatet fra preparePush(); uden det
// falder vi tilbage til at forberede undervejs (virker paa desktop).
export const subscribeToPush = async prepared => {
  const { reg, publicKey } = prepared || (await preparePush());

  const subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  const saveRes = await fetch(`${apiBaseUrl()}/api/push-subscribe`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subscription: subscription.toJSON() }),
  });
  if (!saveRes.ok) {
    // Gem fejlede -> ryd op lokalt, saa enheden ikke staar med et forældreloest abonnement.
    await subscription.unsubscribe().catch(() => {});
    throw new Error('save-failed');
  }

  return subscription;
};

export const unsubscribeFromPush = async () => {
  const subscription = await getExistingPushSubscription();
  if (!subscription) return;

  const { endpoint } = subscription;
  await subscription.unsubscribe().catch(() => {});
  await fetch(`${apiBaseUrl()}/api/push-unsubscribe`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  }).catch(() => {});
};
